import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Button, Paper,
} from '@mui/material';
import { TaskAlertCard, PayrollAlertCard } from '../../components/AlertCards';
import {
  People, Work, Payment, Add, Agriculture, Receipt, RequestPage, AccountBalance,
  AttachMoney, TrendingUp, TrendingDown, Landscape, History,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts';
import {
  dashboardService, activityFeedService,
  type DashboardStats, type MonthlySummary, type PayrollAlert, type TaskDueAlert,
} from '@smartgrow/shared';

const fmtActivityDate = (s: string) => new Date(s).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
const activityTypeLabel = (t: string) => t.replace(/_/g, ' ');

const emptyStats: DashboardStats = { activeProjects: 0, totalPayments: 0, activeStaff: 0, pendingRequests: 0, activeFarmOperations: 0, pendingExpenses: 0, totalRevenue: 0, totalCost: 0, netProfit: 0, roiPercent: 0, cultivatedAcres: 0 };
const emptyMonthlySummary: MonthlySummary = { totalExpenses: 0, salaryPayments: 0, contractorFees: 0, otherExpenses: 0, periodLabel: 'This Month' };
const fmt = (n: number) => `₦${n.toLocaleString('en-NG')}`;
const fmtPct = (n: number) => `${n.toFixed(1)}%`;
// Validated categorical palette (dataviz skill — passes CVD/contrast checks on white surface)
const CHART_COLOR = '#2a78d6';

interface StatCardProps { title: string; value: string | number; icon: ReactNode; color: string; onClick?: () => void; }
const StatCard = ({ title, value, icon, color, onClick }: StatCardProps) => (
  <Card sx={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography color="text.secondary" gutterBottom variant="body2">{title}</Typography>
          <Typography variant="h4" fontWeight="bold">{value}</Typography>
        </Box>
        <Box sx={{ backgroundColor: color, borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const userData = localStorage.getItem('user_data');
  const user = userData ? JSON.parse(userData) : null;

  const { data: stats = emptyStats } = useQuery<DashboardStats>({ queryKey: ['dashboardStats'], queryFn: dashboardService.getDashboardStats, placeholderData: emptyStats });
  const { data: monthlySummary = emptyMonthlySummary } = useQuery<MonthlySummary>({ queryKey: ['monthlySummary'], queryFn: dashboardService.getMonthlySummary, placeholderData: emptyMonthlySummary });
  const { data: payrollAlerts = [] } = useQuery<PayrollAlert[]>({ queryKey: ['payrollAlerts'], queryFn: dashboardService.getPayrollAlerts, placeholderData: [] });
  const { data: taskAlerts = [] } = useQuery<TaskDueAlert[]>({ queryKey: ['taskDueAlerts'], queryFn: dashboardService.getTaskDueAlerts, placeholderData: [] });
  const { data: recentActivity = [] } = useQuery({ queryKey: ['recentActivity'], queryFn: () => activityFeedService.getRecent(8), placeholderData: [] });

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>Admin Dashboard</Typography>
          <Typography variant="body1" color="text.secondary">Welcome, {user?.fullName}. Manage daily farm operations.</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/dashboard/payments/new')}>New Payment</Button>
      </Box>

      {/* Actionable stat cards — clicking navigates to relevant section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard title="Active Projects" value={stats.activeProjects} icon={<Work sx={{ color: 'white' }} />} color="#2E7D32" onClick={() => navigate('/dashboard/projects')} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard title="Pending Requests" value={stats.pendingRequests} icon={<RequestPage sx={{ color: 'white' }} />} color="#D32F2F" onClick={() => navigate('/dashboard/payments/requests')} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard title="Pending Expenses" value={stats.pendingExpenses ?? 0} icon={<Receipt sx={{ color: 'white' }} />} color="#E65100" onClick={() => navigate('/dashboard/expenses')} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard title="Total Payments" value={`₦${stats.totalPayments.toLocaleString()}`} icon={<Payment sx={{ color: 'white' }} />} color="#1976D2" onClick={() => navigate('/dashboard/payments')} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard title="Active Farm Ops" value={stats.activeFarmOperations ?? 0} icon={<Agriculture sx={{ color: 'white' }} />} color="#558B2F" onClick={() => navigate('/dashboard/farm')} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard title="Active Staff" value={stats.activeStaff} icon={<People sx={{ color: 'white' }} />} color="#F57C00" onClick={() => navigate('/dashboard/staff')} />
        </Grid>
      </Grid>

      {/* Executive KPIs — lifetime totals, same figures as the Reports page overview */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Farm Executive Summary (lifetime)</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={6} md={2.4}>
          <StatCard title="Total Revenue" value={fmt(stats.totalRevenue)} icon={<AttachMoney sx={{ color: 'white' }} />} color="#2E7D32" onClick={() => navigate('/dashboard/reports')} />
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <StatCard title="Total Cost" value={fmt(stats.totalCost)} icon={<TrendingDown sx={{ color: 'white' }} />} color="#D32F2F" onClick={() => navigate('/dashboard/reports')} />
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <StatCard title="Net Profit" value={fmt(stats.netProfit)} icon={<AccountBalance sx={{ color: 'white' }} />} color={stats.netProfit >= 0 ? '#1976D2' : '#E65100'} onClick={() => navigate('/dashboard/reports')} />
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <StatCard title="ROI" value={fmtPct(stats.roiPercent)} icon={<TrendingUp sx={{ color: 'white' }} />} color={stats.roiPercent >= 0 ? '#2E7D32' : '#D32F2F'} onClick={() => navigate('/dashboard/reports')} />
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <StatCard title="Cultivated Acres" value={stats.cultivatedAcres.toLocaleString()} icon={<Landscape sx={{ color: 'white' }} />} color="#00838F" onClick={() => navigate('/dashboard/farm')} />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Left: alerts */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Task Alerts</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              {taskAlerts.length === 0
                ? <Typography variant="body2" color="text.secondary">No tasks approaching due date or overdue.</Typography>
                : taskAlerts.map((a) => (
                  <TaskAlertCard key={`${a.taskId}-${a.severity}`} alert={a} onClick={() => navigate(`/dashboard/farm/${a.operationId}`)} />
                ))}
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Payroll Alerts</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              {payrollAlerts.length === 0
                ? <Typography variant="body2" color="text.secondary">No salary payments due or overdue.</Typography>
                : payrollAlerts.map((a) => (
                  <PayrollAlertCard key={`${a.employeeId}-${a.severity}`} alert={a} />
                ))}
            </Box>
          </Paper>

          <Paper sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <History fontSize="small" />
              <Typography variant="h6" fontWeight="bold">Recent Activity</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              {recentActivity.length === 0
                ? <Typography variant="body2" color="text.secondary">No recent activity.</Typography>
                : recentActivity.map((a) => (
                  <Box key={`${a.type}-${a.id}`} sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" fontWeight="medium" noWrap>{a.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {a.projectName ?? 'Unassigned'} · {activityTypeLabel(a.type)}{a.actorName ? ` · ${a.actorName}` : ''} · {fmtActivityDate(a.occurredAt)}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Paper>
        </Grid>

        {/* Right: quick actions + monthly summary */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Quick Actions</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Button variant="outlined" fullWidth startIcon={<Add />} onClick={() => navigate('/dashboard/payments/requests/new')}>New Payment Request</Button>
              <Button variant="outlined" fullWidth startIcon={<Receipt />} onClick={() => navigate('/dashboard/expenses')}>Review Expenses</Button>
              <Button variant="outlined" fullWidth startIcon={<Payment />} onClick={() => navigate('/dashboard/payments/payroll')}>Process Payroll</Button>
              <Button variant="outlined" fullWidth startIcon={<AccountBalance />} onClick={() => navigate('/dashboard/payments/disbursement')}>Fund Disbursement</Button>
              <Button variant="outlined" fullWidth startIcon={<Work />} onClick={() => navigate('/dashboard/projects/new')}>Create Project</Button>
              <Button variant="outlined" fullWidth startIcon={<People />} onClick={() => navigate('/dashboard/employees')}>Manage Employees</Button>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>{monthlySummary.periodLabel}</Typography>
            {monthlySummary.totalExpenses === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No payments recorded yet.</Typography>
            ) : (
              <>
                <Box sx={{ width: '100%', height: 160, mt: 1 }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={[
                        { label: 'Salary', value: monthlySummary.salaryPayments },
                        { label: 'Contractor', value: monthlySummary.contractorFees },
                        { label: 'Other', value: monthlySummary.otherExpenses },
                      ]}
                      layout="vertical"
                      margin={{ left: 8, right: 24 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" horizontal={false} />
                      <XAxis type="number" tickFormatter={(v) => `₦${(v / 1000).toLocaleString()}k`} tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="label" width={80} tick={{ fontSize: 13 }} />
                      <RechartsTooltip formatter={(v: number) => fmt(v)} />
                      <Bar dataKey="value" fill={CHART_COLOR} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                  {[
                    { label: 'Total Expenses', value: monthlySummary.totalExpenses },
                    { label: 'Salary Payments', value: monthlySummary.salaryPayments },
                    { label: 'Contractor Fees', value: monthlySummary.contractorFees },
                    { label: 'Other Expenses', value: monthlySummary.otherExpenses },
                  ].map((row) => (
                    <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                      <Typography variant="body2" fontWeight="bold">{fmt(row.value)}</Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
